import { ReactElement, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { Prompt, SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import styles from './MovePrompt.module.scss';
import { useForm } from 'react-hook-form';
import { moveMenuItemToSection } from 'src/api/moveMenuItemToSection/moveMenuItemToSection';
import { getHierarchyNames } from 'src/helpers';
import { flattenCategoryHierarchy } from './utilities/flattenCategoryHierarchy/flattenCategoryHierarchy';

interface MovePromptProps {
  title?: string;
  menuId: string;
  itemId: string;
  sectionId: string;
  closePrompt?: () => void;
  open: boolean;
}

const MovePrompt = ({ title, menuId, itemId, sectionId, closePrompt, open }: MovePromptProps): ReactElement => {
  const queryClient = useQueryClient();
  const { data: currentMenu } = useMenu(menuId);
  const { showSnackbar } = useSnackbar();

  const sections = useMemo(
    () =>
      flattenCategoryHierarchy(currentMenu.sections)
        .filter((section) => section.sectionId !== sectionId)
        .map((section) => ({
          namesHierarchy: getHierarchyNames(section, currentMenu.sections, {
            id: 'sectionId',
            parent_id: 'parentSectionId',
          }),
          sectionId: section.sectionId,
        })),
    [currentMenu],
  );

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<any>({
    defaultValues: { section: '' },
    mode: 'onChange',
  });

  const selectValue = watch('section');

  const moveButtonHandler = async (data: { section: string }) => {
    try {
      const newSectionId = data.section === 'Uncategorised' ? null : data.section;
      let newDisplayOrder = 0;
      if (newSectionId) {
        const selectedSection = currentMenu.sections.find((section) => section.sectionId === newSectionId);
        newDisplayOrder =
          selectedSection.items.length > 0
            ? Math.max(...selectedSection.items.map((item) => item.displayOrder)) + 1
            : 0;
      } else {
        newDisplayOrder =
          currentMenu.uncategorised.length > 0
            ? Math.max(...currentMenu.uncategorised.map((item) => item.displayOrder)) + 1
            : 0;
      }

      await moveMenuItemToSection(menuId, itemId, newSectionId, newDisplayOrder);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  return (
    <Prompt
      data-testid="close-move-menu-item-prompt"
      closePrompt={closePrompt}
      open={open}
      buttonHandler={handleSubmit(moveButtonHandler)}
      buttonText="Save"
      promptHeading={`Move "${title}" product?`}
      size="small"
      isSubmitting={isSubmitting}
      backButton
      backButtonText="Back"
    >
      <div className={styles.container}>
        <form onSubmit={handleSubmit(moveButtonHandler)}>
          <div className={styles.formControl}>
            <label htmlFor="move-section-dropdown" className={styles.label}>
              Move to category
            </label>
            <select
              data-testid="move-section-dropdown-list"
              id="move-section-dropdown"
              className={styles.select}
              value={selectValue}
              {...register('section')}
            >
              <option value="" disabled>
                Select a category
              </option>

              {sectionId && <option value="Uncategorised">Uncategorised</option>}
              {sections.map(({ sectionId: id, namesHierarchy }) => (
                <option
                  key={`move-prompt-option-${id ?? 'uncategorised'}-${itemId}`}
                  value={id}
                  className={styles.menuItem}
                >
                  {namesHierarchy.slice(0, 2).join(' > ')}
                </option>
              ))}
            </select>
          </div>
        </form>
      </div>
    </Prompt>
  );
};

export default MovePrompt;
